
import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Login() {
    const { t } = useTranslation();
    const { session } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [verifying, setVerifying] = useState(false);
    const [otp, setOtp] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (session) {
            navigate('/app');
        } else {
            // Ensure clean slate for new login attempts if not redirected
            // Note: failing to clear might preserve bad state on some devices
            // but we don't want to clear if we just landed here? 
            // actually, let's only rely on the SignOut button for explicit clearing.
            // Leaving this effect as is for now.
        }
    }, [session, navigate]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            // Check for specific "Email not confirmed" error
            if (error.message.includes("Email not confirmed")) {
                setVerifying(true);
                // Optionally auto-resend code here, but better to let user choose
                setError("Please verify your email to log in.");
                handleResendcode(); // Auto-resend for better UX? Let's do it and notify.
            } else {
                setError(error.message);
            }
        } else {
            navigate('/app');
        }
        setLoading(false);
    };

    const handleResendcode = async () => {
        const { error } = await supabase.auth.resend({
            type: 'signup',
            email: email,
        });
        if (error) setError(error.message);
        else alert(`A new confirmation code has been sent to ${email}`);
    }

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // For login verification, we use type 'signup' (verifying the signup)
        const { error } = await supabase.auth.verifyOtp({
            email,
            token: otp,
            type: 'signup'
        });

        if (error) {
            setError(error.message);
        } else {
            alert('Email verified! Logging you in...');
            // After verify, we must sign in again to get the session
            const { error: loginError } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (loginError) {
                setError("Verification successful, but login failed: " + loginError.message);
            } else {
                navigate('/app');
            }
        }
        setLoading(false);
    }

    if (verifying) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-950 text-white relative overflow-hidden">
                {/* Background effects */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/10 blur-[100px] rounded-full" />

                <div className="bg-stone-900/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl w-full max-w-md border border-stone-800 relative z-10">
                    <h2 className="text-2xl font-bold mb-6 text-center text-white">{t('auth.verify_title')}</h2>
                    <p className="text-center text-stone-400 text-sm mb-6">{t('auth.verify_desc')} {email}</p>
                    {error && <div className="bg-red-900/50 text-red-200 p-3 rounded-xl mb-4 text-sm border border-red-800/50">{error}</div>}
                    <form onSubmit={handleVerifyOtp} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-stone-400 mb-1">{t('auth.verify_code')}</label>
                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                className="w-full bg-black border border-stone-800 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-center text-2xl tracking-widest text-white transition-all shadow-inner"
                                placeholder="000000"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg shadow-indigo-900/20 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                        >
                            {loading ? t('auth.verifying') : t('auth.verify_login_btn')}
                        </button>
                    </form>
                    <button
                        onClick={handleResendcode}
                        className="w-full mt-6 text-sm text-stone-400 hover:text-white transition-colors"
                    >
                        {t('auth.resend_code')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-stone-950 text-white relative overflow-hidden">
            {/* Background effects */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/10 blur-[100px] rounded-full" />

            <div className="bg-stone-900/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl w-full max-w-md border border-stone-800 relative z-10">
                <h2 className="text-2xl font-bold mb-8 text-center text-white space-x-2">
                    <span className="text-indigo-500 tracking-widest uppercase text-lg">Dotting</span>
                </h2>
                {error && <div className="bg-red-900/50 text-red-200 p-3 rounded-xl mb-6 text-sm border border-red-800/50">{error}</div>}

                <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                        <label className="block text-xs uppercase tracking-wider font-bold text-stone-500 mb-2">{t('auth.email')}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-black border border-stone-800 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:outline-none text-white transition-all shadow-inner"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wider font-bold text-stone-500 mb-2">{t('auth.password')}</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-black border border-stone-800 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:outline-none text-white transition-all shadow-inner"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-white hover:bg-gray-200 text-black font-bold py-3 px-4 rounded-xl transition-all shadow-[0_0_20px_-5px_rgba(255,255,255,0.3)] mt-6 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                    >
                        {loading ? t('auth.logging_in') : t('auth.login_btn')}
                    </button>
                </form>

                <p className="mt-8 text-center text-sm text-stone-500">
                    {t('auth.no_account')} <Link to="/register" className="text-white font-medium hover:text-indigo-400 transition-colors ml-1">{t('auth.register_link')}</Link>
                </p>
            </div>
        </div>
    );
}
